import React from 'react';
import { cn } from '~/lib/utils';
import styles from './ComposerTextRow.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * The five heights the composer is drawn at.
 *
 * Every one of them is `16 + n * 24 + 14`: the row's own top padding, n lines of the 15/24 body
 * type, and 14px of slack under the last line. So 54 is one line, 78 is two, 102 three, 126 four,
 * 174 six. The design skips five (150) — no artboard needed it, not because the composer jumps.
 */
export type ComposerTextRowHeight = 54 | 78 | 102 | 126 | 174;

interface ComposerTextRowBaseProps {
  /**
   * The drawn height. Fixed, not content-derived: the 14px of slack under the last line is part
   * of the box (see the stylesheet), so a content-sized row would sit 14px short of the design.
   * Growing between two of these values is the `--cv15-dur-grow` transition.
   */
  height?: ComposerTextRowHeight;
  theme?: ChatTheme;
  className?: string;
}

/**
 * Three content models, and they really are three.
 *
 * `placeholder` and `text` are the same string in two tones — hint before you type, subtle after —
 * which is one of the two things `Chat/Typing` exists to prove. `flow` is the wrapping
 * row that holds `MentionChip`s, literal text runs and the `Caret`, and it is why this row is a
 * flex container rather than a `<p>`.
 */
export type ComposerTextRowProps = ComposerTextRowBaseProps &
  (
    | { content: 'placeholder'; text: string; children?: never }
    | { content: 'text'; text: string; children?: never }
    | { content: 'flow'; text?: never; children: React.ReactNode }
  );

/**
 * The composer's input area — everything above the toolbar.
 *
 * Seventeen `Chat` artboards, four `Queue`, five `Use case` and every conversation composer draw
 * this same box: `padding: 16px 16px 0`, a 15/24 −0.01em block inside it, and a height that steps
 * 54 → 78 → 102 → 126 → 174 as the text wraps. The card that contains it is that height plus the
 * 56px toolbar, so the ceiling is a **230px** card — `Chat/Long text`'s caption says 220, the
 * artboard and the scrollbar's `bottom: 62px` inset both say 230, and 230 is the contract
 * (DECISIONS §2).
 *
 * It is presentational. Paper draws a static string because a real caret does not survive a
 * screenshot, so this row renders one too; the live composer's `<textarea>` is `Composer`'s
 * concern, and `flow` is the shape that row has to hand back.
 *
 * Artboards: all 17 `Chat` boards · all 4 `Queue` boards · all 5 `Use case` boards.
 */
export function ComposerTextRow(props: ComposerTextRowProps) {
  const { height = 54, theme: themeProp, className } = props;
  const theme = useV15Theme(themeProp);
  const isFlow = props.content === 'flow';

  return (
    <div
      className={cn(
        chatThemeClass(theme),
        styles.row,
        // The file adds `overflow: clip` exactly where content can outrun the box: the chip flow,
        // whose wrap is unbounded, and the 174px ceiling, where the text scrolls under a
        // `ComposerScrollbar`. Everywhere else the row is sized to its content and clipping would
        // be a no-op that could still catch a future child's focus ring.
        (isFlow || height === 174) && styles.clip,
        className,
      )}
      // Runtime value into CSS — the only thing inline `style` is for here.
      style={{ '--cv15-composer-text-h': `${height}px` } as React.CSSProperties}
    >
      <div
        className={cn(
          styles.block,
          props.content === 'placeholder' ? styles.placeholder : styles.typed,
        )}
      >
        {isFlow ? <span className={styles.flow}>{props.children}</span> : props.text}
      </div>
    </div>
  );
}
